<template>
  <div
    ref="outerContainerRef"
    class="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-group tw-relative"
    :class="[
      backgroundClasses,
      dimmedClasses,
      higlightedClasses,
      slimClasses,
      paddingClasses,
      shadowClasses,
    ]"
    @click.stop="handleClick"
  >
    <slot></slot>
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { commsTheme } from '@app/shared/comms-theme'

interface Props {
  dimmed?: boolean
  highlighted?: boolean
  subtleBackground?: boolean
  slim?: boolean
  // Drops the container's own padding: every section then owns its own, and
  // can run edge to edge.
  bleed?: boolean
}

const { dimmed, highlighted, subtleBackground, slim, bleed } =
  defineProps<Props>()

const theme = commsTheme()

const outerContainerRef = ref<HTMLElement | null>(null)

const emit =
  defineEmits<{
    click: [e: Event]
    clickOutside: [e: Event]
  }>()

const backgroundClasses = computed(() => ({
  [theme.cardSubtleBackground]: subtleBackground,
  'tw-bg-sf-snow-lighter': !subtleBackground,
}))

const dimmedClasses = computed(() => ({
  'tw-opacity-50': dimmed,
}))

const higlightedClasses = computed(() => ({
  'tw-border-sf-primary-dark': highlighted,
  'tw-shadow-highlight': highlighted,
  [theme.cardBorder]: !highlighted,
}))

const slimClasses = computed(() => ({
  'tw-border tw-border-solid': !slim || theme.newLayout,
  [theme.cardSlimHover]: slim,
}))

// Two shadow classes on one element do not add up: the last one in the
// stylesheet wins. Without !highlighted this would paint over the glow.
const shadowClasses = computed(() => ({
  'tw-shadow-sm': theme.newLayout && !highlighted,
}))

const paddingClasses = computed(() => ({
  'tw-px-6': !bleed,
  'tw-py-4': !bleed && !slim,
  'tw-pt-4': !bleed && slim,
}))

function handleClick(e: Event) {
  emit('click', e)
}

function handleClickOutside(e: Event) {
  if (highlighted) {
    emit('clickOutside', e)
  }
}

onClickOutside(outerContainerRef, handleClickOutside)
</script>
